import React, { useCallback, useEffect, useState } from "react";
import { ShieldCheck, ShieldX, Loader2, History } from "lucide-react";
import { toast } from "sonner";
import { apiClient } from "@/lib/apiClient";

const fmt = (v) => `CHF ${Number(v || 0).toLocaleString("de-CH", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const confidenceTone = (c) => (c >= 0.9 ? "text-emerald-700 bg-emerald-50" : c >= 0.6 ? "text-amber-700 bg-amber-50" : "text-red-700 bg-red-50");

export const AiProposalCards = ({ companyId, sessionId, refreshToken, onChanged }) => {
  const [items, setItems] = useState([]);
  const [busy, setBusy] = useState(null);
  const [rejecting, setRejecting] = useState(null);
  const [reason, setReason] = useState("");

  const load = useCallback(async () => {
    if (!companyId || !sessionId) return;
    try {
      const r = await apiClient.get(`/companies/${companyId}/ai-proposals`, { params: { status: "pending", session_id: sessionId } });
      setItems(r.data || []);
    } catch { setItems([]); }
  }, [companyId, sessionId]);

  useEffect(() => { load(); }, [load, refreshToken]);
  useEffect(() => {
    const id = setInterval(load, 8000);
    return () => clearInterval(id);
  }, [load]);

  const act = async (id, action, rejectReason = "") => {
    setBusy(id + action);
    try {
      const body = action === "reject" ? { reason: rejectReason.trim() || "Im Chat abgelehnt (ohne Begründung)" } : {};
      const r = await apiClient.post(`/companies/${companyId}/ai-proposals/${id}/${action}`, body);
      toast.success(action === "approve" ? `Ausgeführt${r.data?.result?.journal_entry_id ? ` – Buchung ${r.data.result.journal_entry_id.slice(0, 8)}…` : ""}` : "Vorschlag abgelehnt");
      setRejecting(null);
      setReason("");
      await load();
      onChanged?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Aktion fehlgeschlagen");
      await load();
    } finally { setBusy(null); }
  };

  if (!items.length) return null;
  return (
    <div className="space-y-2 px-3 pb-2" data-testid="ai-proposal-cards">
      {items.map((p) => (
        <div key={p.id} className="rounded-xl border border-amber-300 bg-amber-50/60 p-3 text-xs" data-testid={`ai-proposal-${p.id}`}>
          <div className="flex items-center justify-between gap-2">
            <span className="font-semibold text-slate-800">Freigabe erforderlich · {p.tool}</span>
            <span className={`rounded-full px-2 py-0.5 font-mono ${confidenceTone(p.confidence)}`} data-testid={`ai-proposal-confidence-${p.id}`}>
              Konfidenz {Number(p.confidence).toFixed(2)}
            </span>
          </div>
          <div className="mt-1 text-slate-700" data-testid={`ai-proposal-summary-${p.id}`}>{p.summary}</div>
          {p.amount > 0 && <div className="mt-1 font-semibold" data-testid={`ai-proposal-amount-${p.id}`}>{fmt(p.amount)}</div>}
          <div className="mt-1 text-[11px] text-slate-500">{p.decision_reason}{p.confidence_reasons?.length ? ` · ${p.confidence_reasons.join(", ")}` : ""}</div>
          {p.rejection_hints?.length > 0 && (
            <div className="mt-2 rounded-lg border border-rose-200 bg-rose-50 px-2.5 py-2 text-[11px] text-rose-800" data-testid={`ai-proposal-rejection-hint-${p.id}`}>
              <div className="flex items-center gap-1 font-semibold"><History className="h-3 w-3" /> Aus Ablehnungen gelernt</div>
              {p.rejection_hints.map((h) => (
                <div key={h.proposal_id} className="mt-0.5">
                  Ähnlicher Vorschlag am {h.rejected_at ? new Date(h.rejected_at).toLocaleDateString("de-CH") : "–"} abgelehnt ({h.match})
                  {h.reason ? <>: «<span className="italic">{h.reason}</span>»</> : " – ohne Begründung"}
                </div>
              ))}
            </div>
          )}
          <div className="mt-2 flex gap-2">
            <button type="button" disabled={!!busy} onClick={() => act(p.id, "approve")} data-testid={`ai-proposal-approve-${p.id}`}
              className="inline-flex items-center gap-1 rounded-full bg-emerald-600 px-3 py-1 font-medium text-white transition-colors hover:bg-emerald-700 disabled:opacity-50">
              {busy === p.id + "approve" ? <Loader2 className="h-3 w-3 animate-spin" /> : <ShieldCheck className="h-3 w-3" />} Freigeben & ausführen
            </button>
            <button type="button" disabled={!!busy} onClick={() => { setRejecting(rejecting === p.id ? null : p.id); setReason(""); }} data-testid={`ai-proposal-reject-${p.id}`}
              className="inline-flex items-center gap-1 rounded-full border border-slate-300 bg-white px-3 py-1 font-medium text-slate-700 transition-colors hover:bg-slate-100 disabled:opacity-50">
              <ShieldX className="h-3 w-3" /> Ablehnen
            </button>
          </div>
          {rejecting === p.id && (
            <div className="mt-2 space-y-1.5" data-testid={`ai-proposal-reject-form-${p.id}`}>
              <input
                type="text"
                value={reason}
                maxLength={300}
                autoFocus
                onChange={(e) => setReason(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") act(p.id, "reject", reason); }}
                placeholder="Grund (optional), z. B. falsches Konto, Betrag stimmt nicht …"
                className="w-full rounded-lg border border-slate-300 bg-white px-2.5 py-1.5 text-xs outline-none focus:border-slate-500"
                data-testid={`ai-proposal-reject-reason-${p.id}`}
              />
              <div className="flex gap-2">
                <button type="button" disabled={!!busy} onClick={() => act(p.id, "reject", reason)} data-testid={`ai-proposal-reject-confirm-${p.id}`}
                  className="inline-flex items-center gap-1 rounded-full bg-slate-800 px-3 py-1 font-medium text-white transition-colors hover:bg-slate-900 disabled:opacity-50">
                  {busy === p.id + "reject" ? <Loader2 className="h-3 w-3 animate-spin" /> : <ShieldX className="h-3 w-3" />} Ablehnung bestätigen
                </button>
                <button type="button" disabled={!!busy} onClick={() => { setRejecting(null); setReason(""); }} data-testid={`ai-proposal-reject-cancel-${p.id}`}
                  className="rounded-full px-3 py-1 font-medium text-slate-600 transition-colors hover:bg-slate-100">
                  Abbrechen
                </button>
              </div>
            </div>
          )}
        </div>
      ))}
    </div>
  );
};
